import type {
	RecommendationContext,
	RecommendationContextAxis,
	RecommendationContextCell,
} from "@memorang/types";
import type {
	AxisCoordinate,
	AxisFilters,
	CellMastery,
	FullCell,
} from "../contract/recommendation-context-types";

const compareIds = (left: number | string, right: number | string): number => {
	if (typeof left === "number" && typeof right === "number") {
		return left - right;
	}
	const leftText = String(left);
	const rightText = String(right);
	return leftText === rightText ? 0 : leftText < rightText ? -1 : 1;
};

const coordinatesOf = (cell: RecommendationContextCell): AxisCoordinate =>
	Object.fromEntries(
		cell.coordinates.map(({ axisId, valueId }) => [axisId, valueId]),
	);

export const trackKey = (
	cell: FullCell,
	trackAxes: RecommendationContextAxis[],
): string =>
	trackAxes.map(({ id }) => `${id}:${cell.coordinates[id]}`).join("|");

const masteryOf = (
	cell: RecommendationContextCell,
	context: RecommendationContext,
): CellMastery => {
	const count = cell.evidence.distinctItemCount;
	const evidenceStatus =
		count === 0
			? "NO_EVIDENCE"
			: count < context.policy.minimumEvidence
				? "PROVISIONAL"
				: "ELIGIBLE";
	const value = cell.evidence.rollingMastery30;
	return {
		value,
		distinctItemCount: count,
		evidenceStatus,
		lastAnsweredAt: cell.evidence.lastAnsweredAt,
		passed:
			evidenceStatus === "ELIGIBLE" &&
			value !== null &&
			value >= context.policy.passingMastery,
	};
};

export const fullCells = (context: RecommendationContext): FullCell[] => {
	const expected = new Set(context.axes.map(({ id }) => id));
	return context.cells.flatMap((source) => {
		const actual = new Set(source.coordinates.map(({ axisId }) => axisId));
		if (
			actual.size !== expected.size ||
			![...expected].every((axisId) => actual.has(axisId))
		) {
			return [];
		}
		return [
			{
				source,
				coordinates: coordinatesOf(source),
				mastery: masteryOf(source, context),
			},
		];
	});
};

export const matchesFilters = (
	cell: FullCell,
	filters?: AxisFilters,
): boolean =>
	Object.entries(filters ?? {}).every(
		([axisId, values]) =>
			values.length === 0 || values.includes(cell.coordinates[axisId]),
	);

export const stableCellOrder =
	(axes: RecommendationContextAxis[]) =>
	(left: FullCell, right: FullCell): number => {
		for (const axis of axes) {
			const order = new Map(
				axis.values.map(({ id, sortOrder }) => [id, sortOrder]),
			);
			const difference =
				(order.get(left.coordinates[axis.id]) ?? Number.MAX_SAFE_INTEGER) -
					(order.get(right.coordinates[axis.id]) ?? Number.MAX_SAFE_INTEGER) ||
				compareIds(left.coordinates[axis.id], right.coordinates[axis.id]);
			if (difference !== 0) {
				return difference;
			}
		}
		return compareIds(left.source.key, right.source.key);
	};
